import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  CornerDownRight,
  Lock,
  LogIn,
  MessageSquare,
  Send,
  Trash2,
  User as UserIcon,
  X
} from "lucide-react";
import { apiGet, apiSend } from "../services/api";
import { useAuth } from "../services/auth";
import type { Article, Comment } from "../types";
import { formatDate } from "../utils/date";

interface CommentsSectionProps {
  article: Article;
}

export function CommentsSection({ article }: CommentsSectionProps) {
  const { user, login } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New comment state
  const [commentBody, setCommentBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [replyTo, setReplyTo] = useState<Comment | null>(null);

  // Inline sign-in state for unauthenticated readers
  const [showInlineLogin, setShowInlineLogin] = useState(false);
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const commentsAllowed = article.allow_comments !== false;

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiGet<Comment[]>(`/api/engagement/comments/article/${article.id}`);
      setComments(data || []);
    } catch (err: any) {
      console.error("Failed to load comments:", err);
      // Fallback empty if no comments table yet
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (article.id) {
      fetchComments();
    }
  }, [article.id, user]);

  const handleInlineLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginIdentifier.trim() || !loginPassword) {
      setLoginError("Please provide your username/email and password.");
      return;
    }

    try {
      setLoginLoading(true);
      setLoginError(null);
      await login(loginIdentifier.trim(), loginPassword);
      setShowInlineLogin(false);
      setLoginIdentifier("");
      setLoginPassword("");
    } catch (err: any) {
      setLoginError(err.message || "Failed to sign in. Please verify your credentials.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in to submit a comment.");
      return;
    }

    if (!commentBody.trim()) {
      setError("Please write a comment before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const createdComment = await apiSend<Comment>("/api/engagement/comments", {
        method: "POST",
        body: JSON.stringify({
          article_id: article.id,
          parent_id: replyTo ? replyTo.id : null,
          body: commentBody.trim()
        })
      });

      // Optimistically update list
      setComments((prev) => [...prev, createdComment]);
      setCommentBody("");
      setReplyTo(null);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (err: any) {
      setError(err.message || "Failed to post comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm("Are you sure you want to remove this comment?")) return;
    try {
      await apiSend(`/api/engagement/comments/${commentId}`, { method: "DELETE" });
      setComments((prev) => prev.filter((c) => c.id !== commentId && c.parent_id !== commentId));
    } catch (err: any) {
      alert(err.message || "Failed to remove comment.");
    }
  };

  // Group comments into root threads and replies
  const rootComments = comments.filter((c) => !c.parent_id);
  const getReplies = (parentId: number) => comments.filter((c) => c.parent_id === parentId);

  return (
    <section className="mag-comments-section" id="reader-comments" aria-label="Reader Comments">
      <div className="mag-comments-header-row">
        <div className="mag-comments-title-wrap">
          <MessageSquare className="text-amber-600" size={20} />
          <h3 className="mag-comments-title">READER DISCUSSION</h3>
          <span className="mag-comments-count-badge">
            {comments.length} {comments.length === 1 ? "Contribution" : "Contributions"}
          </span>
        </div>
      </div>

      {/* Closed comments notice */}
      {!commentsAllowed && (
        <div className="mag-comments-closed-banner">
          <AlertCircle size={18} className="text-stone-500 shrink-0" />
          <p>Comments are closed for this editorial story.</p>
        </div>
      )}

      {/* Comments enabled view */}
      {commentsAllowed && (
        <>
          {/* Submission Form for Authenticated Users */}
          {user ? (
            <div className="mag-comment-form-card">
              <div className="mag-comment-user-identity">
                <div className="mag-comment-user-avatar">
                  {user.name ? user.name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                </div>
                <div className="mag-comment-user-details">
                  <span className="mag-comment-user-name">{user.name || user.username}</span>
                  <span className="mag-comment-user-badge">
                    {user.roles?.includes("Super Administrator") || user.roles?.includes("Administrator")
                      ? "Editorial Staff"
                      : "Verified Reader"}
                  </span>
                </div>
              </div>

              {replyTo && (
                <div className="mag-comment-replying-to">
                  <div className="flex items-center gap-2">
                    <CornerDownRight size={14} className="text-amber-600" />
                    <span>
                      Replying to <strong>{replyTo.author_name}</strong>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    className="mag-comment-cancel-reply"
                    title="Cancel reply"
                  >
                    <X size={14} />
                    <span>Cancel</span>
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmitComment} className="mag-comment-form">
                <textarea
                  className="mag-comment-textarea"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder={
                    replyTo
                      ? `Write your reply to ${replyTo.author_name}...`
                      : "Contribute your perspective or question on this report..."
                  }
                  rows={4}
                  maxLength={5000}
                  disabled={submitting}
                />

                <div className="mag-comment-form-footer">
                  <span className="mag-comment-char-count">
                    {commentBody.length} / 5,000 characters
                  </span>
                  <button
                    type="submit"
                    className="mag-comment-submit-btn"
                    disabled={submitting || !commentBody.trim()}
                  >
                    <Send size={15} />
                    <span>{submitting ? "Publishing..." : replyTo ? "Post Reply" : "Post Comment"}</span>
                  </button>
                </div>
              </form>

              {submitSuccess && (
                <div className="mag-comment-success-msg">
                  <CheckCircle2 size={16} />
                  <span>Your comment has been published to the discussion.</span>
                </div>
              )}

              {error && (
                <div className="mag-comment-error-msg">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
            </div>
          ) : (
            /* Login Requirement Card for Non-Authenticated Users */
            <div className="mag-comment-auth-wall">
              <div className="mag-comment-auth-wall-inner">
                <div className="mag-comment-auth-icon-wrap">
                  <Lock size={22} className="text-amber-600" />
                </div>
                <div className="mag-comment-auth-copy">
                  <h4 className="mag-comment-auth-title">Join the Editorial Discussion</h4>
                  <p className="mag-comment-auth-desc">
                    Reader commentary on ZimSmart Magazine requires an active account to maintain high-quality civil analysis and verified exchange.
                  </p>
                </div>

                {!showInlineLogin ? (
                  <div className="mag-comment-auth-actions">
                    <button
                      type="button"
                      className="mag-auth-btn-primary"
                      onClick={() => setShowInlineLogin(true)}
                    >
                      <LogIn size={15} />
                      <span>Sign In to Comment</span>
                    </button>
                    <Link to="/admin/login" className="mag-auth-link-secondary">
                      <span>Staff Portal &rarr;</span>
                    </Link>
                  </div>
                ) : (
                  /* Quick Inline Reader Sign In */
                  <form onSubmit={handleInlineLogin} className="mag-inline-login-form">
                    <div className="mag-inline-login-header">
                      <span className="mag-inline-login-title">Quick Sign In</span>
                      <button
                        type="button"
                        onClick={() => setShowInlineLogin(false)}
                        className="mag-inline-login-close"
                        aria-label="Close form"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="mag-inline-login-fields">
                      <input
                        type="text"
                        placeholder="Email or Username (e.g. admin)"
                        value={loginIdentifier}
                        onChange={(e) => setLoginIdentifier(e.target.value)}
                        className="mag-inline-input"
                        autoFocus
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="mag-inline-input"
                      />
                      <button
                        type="submit"
                        className="mag-inline-submit-btn"
                        disabled={loginLoading}
                      >
                        {loginLoading ? "Verifying..." : "Sign In & Comment"}
                      </button>
                    </div>

                    {loginError && (
                      <div className="mag-comment-error-msg mt-2">
                        <AlertCircle size={14} />
                        <span>{loginError}</span>
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Comments List */}
          <div className="mag-comments-list-wrap">
            {loading ? (
              <div className="mag-comments-loading">
                <div className="mag-spinner" />
                <span>Loading discussion...</span>
              </div>
            ) : rootComments.length === 0 ? (
              <div className="mag-comments-empty">
                <p>No reader comments yet on this report.</p>
                <p className="mag-comments-empty-sub">
                  {user
                    ? "Share your perspective or ask a question using the form above."
                    : "Sign in to be the first to contribute to this discussion."}
                </p>
              </div>
            ) : (
              <div className="mag-comments-thread-list">
                {rootComments.map((comment) => {
                  const replies = getReplies(comment.id);
                  const isAuthor = user?.id === comment.user_id;
                  const canDelete = isAuthor || user?.permissions?.includes("comments.manage") || user?.permissions?.includes("*");

                  return (
                    <div key={comment.id} className="mag-comment-card">
                      <div className="mag-comment-main">
                        <div className="mag-comment-avatar">
                          {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : "R"}
                        </div>
                        <div className="mag-comment-content-wrap">
                          <div className="mag-comment-meta-bar">
                            <span className="mag-comment-author-name">{comment.author_name}</span>
                            <span className="mag-comment-dot">•</span>
                            <span className="mag-comment-date">{formatDate(comment.created_at)}</span>
                            {comment.user_name && (
                              <span className="mag-comment-verified-tag">Verified Member</span>
                            )}
                          </div>

                          <div className="mag-comment-text">{comment.body}</div>

                          <div className="mag-comment-actions-bar">
                            {user && (
                              <button
                                type="button"
                                onClick={() => {
                                  setReplyTo(comment);
                                  document.getElementById("reader-comments")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="mag-comment-action-btn reply"
                              >
                                <CornerDownRight size={13} />
                                <span>Reply</span>
                              </button>
                            )}

                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => handleDeleteComment(comment.id)}
                                className="mag-comment-action-btn delete"
                                title="Delete comment"
                              >
                                <Trash2 size={13} />
                                <span>Delete</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Nested Replies */}
                      {replies.length > 0 && (
                        <div className="mag-comment-replies-wrap">
                          {replies.map((reply) => {
                            const isReplyAuthor = user?.id === reply.user_id;
                            const canDeleteReply =
                              isReplyAuthor ||
                              user?.permissions?.includes("comments.manage") ||
                              user?.permissions?.includes("*");

                            return (
                              <div key={reply.id} className="mag-comment-reply-item">
                                <div className="mag-comment-avatar reply-avatar">
                                  {reply.author_name ? reply.author_name.charAt(0).toUpperCase() : "R"}
                                </div>
                                <div className="mag-comment-content-wrap">
                                  <div className="mag-comment-meta-bar">
                                    <span className="mag-comment-author-name">{reply.author_name}</span>
                                    <span className="mag-comment-dot">•</span>
                                    <span className="mag-comment-date">{formatDate(reply.created_at)}</span>
                                  </div>

                                  <div className="mag-comment-text">{reply.body}</div>

                                  {canDeleteReply && (
                                    <div className="mag-comment-actions-bar">
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteComment(reply.id)}
                                        className="mag-comment-action-btn delete"
                                        title="Delete reply"
                                      >
                                        <Trash2 size={13} />
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
